/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal functionality for Telephony Servers.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { fetchTelephonyServersQuickList } from '../api';
import { TelephonyServer } from '../types';
import { TelephonyServerFormData } from '../types';

export function useQuickList(
  setFormData: (data: TelephonyServerFormData) => void,
  setOriginalData: (data: TelephonyServerFormData) => void,
) {
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || '';

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListServers, setQuickListServers] = useState<TelephonyServer[]>([]);

  const loadQuickList = useCallback(async () => {
    try {
      const servers = await fetchTelephonyServersQuickList(quickListSearch, tenantSlug, user?.id);
      setQuickListServers(servers);
    } catch (error) {
      setQuickListServers([]);
    }
  }, [quickListSearch, tenantSlug, user?.id]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList) {
      loadQuickList();
    }
  }, [showQuickList, quickListSearch, loadQuickList]);

  // Filter servers client-side
  const filteredQuickList = useMemo(() => {
    if (!quickListSearch.trim()) {
      return quickListServers;
    }
    const searchLower = quickListSearch.toLowerCase();
    return quickListServers.filter(
      (server) =>
        server.name?.toLowerCase().includes(searchLower) ||
        server.host?.toLowerCase().includes(searchLower) ||
        server.description?.toLowerCase().includes(searchLower),
    );
  }, [quickListServers, quickListSearch]);

  const handleQuickListSelect = useCallback(
    (selectedServer: TelephonyServer) => {
      const mappedServer: TelephonyServerFormData = {
        name: selectedServer.name ?? '',
        description: selectedServer.description ?? '',
        host: selectedServer.host ?? '',
        port: selectedServer.port ?? 22,
        username: selectedServer.username ?? '',
        password: '',
        eslHost: selectedServer.eslHost ?? selectedServer.host ?? '',
        freeswitchPort: selectedServer.freeswitchPort ?? 8021,
        freeswitchPassword: '',
        timezone: selectedServer.timezone ?? 'UTC',
        location: selectedServer.location ?? '',
        monitoringEnabled: selectedServer.monitoringEnabled ?? true,
        healthCheckInterval: selectedServer.healthCheckInterval ?? 30,
      };

      setFormData(mappedServer);
      setOriginalData(mappedServer);
      setShowQuickList(false);
      setQuickListSearch('');
    },
    [setFormData, setOriginalData],
  );

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    handleQuickListSelect,
  };
}

